import React, { useEffect, useState, useMemo } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  User,
  Phone,
  MapPin,
  Car,
  CalendarDays,
  CheckCircle,
  Loader2,
  ShieldCheck,
  Trash2,
  Copy,
} from "lucide-react";
import { motion } from "framer-motion";

/* ================= PAGE ================= */
export default function AdminBookings() {
  const [dark, setDark] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  /* ================= THEME ================= */
  useEffect(() => {
    const t = localStorage.getItem("theme");
    setDark(t !== "light");
  }, []);

  const theme = {
    page: dark ? "bg-[#0b0b0c] text-white" : "bg-gray-100 text-gray-900",

    card: dark
      ? "bg-[#111214] border border-white/10 shadow-xl"
      : "bg-white border border-gray-200 shadow-sm",

    muted: dark ? "text-gray-400" : "text-gray-500",

    badge: {
      pending:
        "bg-yellow-500/10 text-yellow-400 border border-yellow-500/20",
      approved:
        "bg-blue-500/10 text-blue-400 border border-blue-500/20",
      completed:
        "bg-green-500/10 text-green-400 border border-green-500/20",
    },
  };

  /* ================= LOAD DATA ================= */
  const loadData = async () => {
    setLoading(true);

    const bookingSnap = await getDocs(
      query(collection(db, "bookings"), orderBy("createdAt", "desc"))
    );

    const carSnap = await getDocs(collection(db, "cars"));

    setCars(carSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

    setBookings(
      bookingSnap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }))
    );

    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  /* ================= ACTIONS ================= */
  const approveBooking = async (booking) => {
    await updateDoc(doc(db, "bookings", booking.id), {
      status: "Approved",
    });
    loadData();
  };

  const markCompleted = async (booking) => {
    await updateDoc(doc(db, "bookings", booking.id), {
      status: "Completed",
    });
    loadData();
  };

  const deleteBooking = async (booking) => {
    if (!window.confirm("Delete this booking permanently?")) return;
    await deleteDoc(doc(db, "bookings", booking.id));
    loadData();
  };

  const copyBooking = (b, car) => {
    const text = `
HEARSE BOOKING DETAILS
----------------------
Name: ${b.name}
Phone: ${b.phone}
Location: ${b.location}

Route: ${b.route}
Event: ${
      b.eventStart && b.eventEnd
        ? `${b.eventStart} → ${b.eventEnd}`
        : "Not specified"
    }

Car: ${car ? `${car.name} (${car.plate})` : "Not assigned"}
Status: ${b.status}
    `.trim();

    navigator.clipboard.writeText(text);
    alert("Booking details copied ✔");
  };

  /* ================= HELPERS ================= */
  const carById = (id) => cars.find((c) => c.id === id);
  const sorted = useMemo(() => bookings, [bookings]);

  /* ================= UI ================= */
  return (
    <div className={`min-h-screen px-8 py-16 ${theme.page}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto"
      >
        {/* HEADER */}
        <div className="mb-10">
          <h2 className="text-4xl font-bold tracking-tight">
            Hearse Bookings
          </h2>
          <p className={`mt-2 ${theme.muted}`}>
            Review, approve, copy or delete bookings
          </p>
        </div>

        {/* CONTENT */}
        <div className={`rounded-3xl p-6 ${theme.card}`}>
          {loading ? (
            <div className="flex items-center justify-center py-24 gap-3">
              <Loader2 className="animate-spin" />
              Loading bookings…
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left opacity-60 border-b border-white/10">
                    <th className="pb-4">Customer</th>
                    <th>Route</th>
                    <th>Event</th>
                    <th>Assigned Car</th>
                    <th>Status</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {sorted.map((b) => {
                    const car = carById(b.carId);

                    return (
                      <tr
                        key={b.id}
                        className="border-b border-white/5 hover:bg-white/[0.03] transition"
                      >
                        {/* CUSTOMER */}
                        <td className="py-5">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2 font-semibold">
                              <User size={14} />
                              {b.name}
                            </div>
                            <div className="flex items-center gap-2 text-xs opacity-60">
                              <Phone size={12} /> {b.phone}
                            </div>
                            <div className="flex items-center gap-2 text-xs opacity-60">
                              <MapPin size={12} /> {b.location}
                            </div>
                          </div>
                        </td>

                        {/* ROUTE */}
                        <td className="max-w-xs truncate">
                          <div className="flex items-center gap-2">
                            <Car size={14} />
                            {b.route}
                          </div>
                        </td>

                        {/* DATE */}
                        <td>
                          <div className="flex items-center gap-2">
                            <CalendarDays size={14} />
                            {b.eventStart && b.eventEnd
                              ? `${b.eventStart} → ${b.eventEnd}`
                              : "—"}
                          </div>
                        </td>

                        {/* CAR */}
                        <td>
                          {car ? (
                            <div className="flex items-center gap-3">
                              <img
                                src={car.image}
                                alt={car.name}
                                className="w-12 h-12 rounded-xl object-cover border border-white/10"
                              />
                              <div>
                                <p className="font-semibold">{car.name}</p>
                                <p className="text-xs opacity-60">
                                  {car.plate}
                                </p>
                              </div>
                            </div>
                          ) : (
                            <span className="opacity-40">Not assigned</span>
                          )}
                        </td>

                        {/* STATUS */}
                        <td>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              b.status === "Completed"
                                ? theme.badge.completed
                                : b.status === "Approved"
                                ? theme.badge.approved
                                : theme.badge.pending
                            }`}
                          >
                            {b.status}
                          </span>
                        </td>

                        {/* ACTIONS */}
                        <td className="text-right space-x-2">
                          <button
                            onClick={() => copyBooking(b, car)}
                            className="inline-flex items-center gap-1 px-3 py-2 rounded-xl text-gray-400 hover:bg-white/10 transition"
                          >
                            <Copy size={14} />
                            Copy
                          </button>

                          {b.status === "Pending" && (
                            <button
                              onClick={() => approveBooking(b)}
                              className="inline-flex items-center gap-1 px-3 py-2 rounded-xl text-blue-400 hover:bg-blue-500/10 transition"
                            >
                              <ShieldCheck size={16} />
                              Approve
                            </button>
                          )}

                          {b.status === "Approved" && (
                            <button
                              onClick={() => markCompleted(b)}
                              className="inline-flex items-center gap-1 px-3 py-2 rounded-xl text-green-400 hover:bg-green-500/10 transition"
                            >
                              <CheckCircle size={16} />
                              Complete
                            </button>
                          )}

                          <button
                            onClick={() => deleteBooking(b)}
                            className="inline-flex items-center gap-1 px-3 py-2 rounded-xl text-red-400 hover:bg-red-500/10 transition"
                          >
                            <Trash2 size={14} />
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
